// hooks/useUnitJwt.ts
import { useState, useEffect, useRef } from 'react';
import { getUnitJwtToken, getUnitCoAccountDetails } from '../services/api/user.service';
import { useLocation } from 'react-router-dom';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { forceUnitCoReload } from '../utils/unitcoReset';
import { useArtist } from '../contexts/ArtistContext';
import { useUser } from '../contexts/UserContext';


// Make keys user-specific to prevent cross-user data leakage
export const getTokenKey = (userId: string) => `unit_jwt_token_${userId}`;
export const getExpKey = (userId: string) => `unit_jwt_exp_${userId}`;
export const getCustomerTokenKey = (userId: string) => `unitCustomerToken_${userId}`;
export const getCustomerTokenExpKey = (userId: string) => `unitCustomerToken_exp_${userId}`;
export const getApiTypeKey = (userId: string) => `apiType_${userId}`;

// Function to clear all user-specific UnitCo data
export const clearUserUnitCoData = (userId: string) => {
  localStorage.removeItem(getTokenKey(userId));
  localStorage.removeItem(getExpKey(userId));
  localStorage.removeItem(getCustomerTokenKey(userId));
  localStorage.removeItem(getCustomerTokenExpKey(userId));
  localStorage.removeItem(getApiTypeKey(userId));
  localStorage.removeItem('unitCustomerToken');
};

export function useUnitJwt(shouldFetch: boolean = true) {
  const { pathname } = useLocation();
  const [jwt, setJwt] = useState<string>('');
  const [jwtFromApi, setJwtFromApi] = useState<boolean>(false);
  const [jwtArtistId, setJwtArtistId] = useState<number | null>(null);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const timerRef = useRef<number | null>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const { currentArtistId } = useArtist();
  const { userProfile } = useUser();
  
  // Use ref to track the last artist ID we fetched for (to detect changes)
  const lastFetchedArtistIdRef = useRef<number | null>(null);

  // Get current user ID from Firebase Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (user) {
        const newUserId = user.uid;
        
        if (currentUserId && currentUserId !== newUserId) {
          clearUserUnitCoData(currentUserId);
          setIsFetching(false);
          setJwt('');
          setJwtFromApi(false);
          setJwtArtistId(null);
          lastFetchedArtistIdRef.current = null;
          forceUnitCoReload();
          return;
        }
        
        setCurrentUserId(newUserId);
      } else {
        if (currentUserId) {
          clearUserUnitCoData(currentUserId);
        }
        
        setCurrentUserId(null);
        setJwt('');
        setJwtFromApi(false);
        setJwtArtistId(null);
        setIsFetching(false);
        lastFetchedArtistIdRef.current = null;
      }
    });
    return () => unsubscribe();
  }, []);

  // Main effect for fetching JWT
  useEffect(() => {
    const isDashboardRoute = pathname === '/dashboard' || pathname === '/analytics';
    const isBankingRoute = pathname === '/banking' || pathname === '/banking/activities';
    const isWhiteLabelRoute = isDashboardRoute || isBankingRoute;
    
    console.log('[useUnitJwt] useEffect triggered - shouldFetch:', shouldFetch, 'currentUserId:', currentUserId, 'currentArtistId:', currentArtistId, 'pathname:', pathname, 'isDashboardRoute:', isDashboardRoute, 'isBankingRoute:', isBankingRoute);
    
    if (!shouldFetch || !currentUserId || !currentArtistId) {
      console.log('[useUnitJwt] Early return - shouldFetch:', shouldFetch, 'currentUserId:', currentUserId, 'currentArtistId:', currentArtistId);
      if (!currentArtistId && isWhiteLabelRoute) {
        setJwt('');
        setJwtFromApi(false);
        setJwtArtistId(null);
      }
      return;
    }
    
    // Dashboard/analytics only: storage-first flow (customer token → JWT → APIs). Do not affect banking.
    if (isDashboardRoute) {
      const buffer = 5 * 60 * 1000; // 5 min
      const artistChanged = lastFetchedArtistIdRef.current !== null && lastFetchedArtistIdRef.current !== currentArtistId;

      // 1. Valid customer token in storage? Use it and skip APIs.
      const storedCust = localStorage.getItem(getCustomerTokenKey(currentUserId));
      const storedCustExp = localStorage.getItem(getCustomerTokenExpKey(currentUserId));
      if (storedCust && storedCustExp) {
        const exp = parseInt(storedCustExp, 10);
        if (exp > Date.now() + buffer) {
          console.log('[useUnitJwt] Dashboard: using valid customer token from storage');
          lastFetchedArtistIdRef.current = currentArtistId;
          const existingJwt = localStorage.getItem(getTokenKey(currentUserId));
          setJwt(existingJwt || '');
          setJwtFromApi(!!existingJwt);
          setJwtArtistId(currentArtistId);
          const tokenEvent = new CustomEvent('unitcoCustomerTokenReady', {
            detail: { userId: currentUserId, customerToken: storedCust }
          });
          window.dispatchEvent(tokenEvent);
          return;
        }
      }

      // 2. Artist changed? Clear cache.
      if (artistChanged) {
        console.log('[useUnitJwt] Dashboard: artist changed, clearing cache');
        setJwt('');
        setJwtFromApi(false);
        setJwtArtistId(null);
        setIsFetching(false);
        lastFetchedArtistIdRef.current = null;
        localStorage.removeItem(getTokenKey(currentUserId));
        localStorage.removeItem(getExpKey(currentUserId));
        localStorage.removeItem(getCustomerTokenKey(currentUserId));
        localStorage.removeItem(getCustomerTokenExpKey(currentUserId));
        localStorage.removeItem(getApiTypeKey(currentUserId));
        localStorage.removeItem('unitCustomerToken');
      }

      // 3. Valid JWT in storage? Fetch customer token only.
      const existingToken = localStorage.getItem(getTokenKey(currentUserId));
      const existingExp = localStorage.getItem(getExpKey(currentUserId));
      if (existingToken && existingExp && !artistChanged) {
        const expTime = parseInt(existingExp);
        if (expTime > Date.now() + buffer) {
          console.log('[useUnitJwt] Dashboard: valid JWT in storage, fetching customer token only');
          lastFetchedArtistIdRef.current = currentArtistId;
          setJwt(existingToken);
          setJwtFromApi(true);
          setJwtArtistId(currentArtistId);
          callUnitCustomerToken(existingToken, currentUserId, 0, true).catch(err => {
            console.error('[useUnitJwt] Failed to get customer token:', err);
          });
          return;
        }
      }

      // 4. Fetch JWT then customer token.
      if (isFetching && lastFetchedArtistIdRef.current === currentArtistId) {
        return;
      }
      setIsFetching(true);
      const artistIdForFetch = currentArtistId;
      lastFetchedArtistIdRef.current = currentArtistId;
      getUnitJwtToken(true, artistIdForFetch, currentUserId)
        .then(({ token, exp }) => {
          if (currentArtistId !== artistIdForFetch) {
            setIsFetching(false);
            setJwt('');
            setJwtFromApi(false);
            setJwtArtistId(null);
            return;
          }
          if (!token || token.trim() === '') {
            setIsFetching(false);
            setJwt('');
            setJwtFromApi(false);
            setJwtArtistId(null);
            return;
          }
          setJwt(token);
          setJwtFromApi(true);
          setJwtArtistId(artistIdForFetch);
          setIsFetching(false);
          const expMs = exp * 1000;
          localStorage.setItem(getTokenKey(currentUserId), token);
          localStorage.setItem(getExpKey(currentUserId), expMs.toString());
          callUnitCustomerToken(token, currentUserId, 0, true).catch(err => {
            console.error('[useUnitJwt] Failed to get customer token:', err);
          });
        })
        .catch((error: any) => {
          console.error('[useUnitJwt] Dashboard JWT fetch failed:', error);
          setIsFetching(false);
          setJwt('');
          setJwtFromApi(false);
          setJwtArtistId(null);
          lastFetchedArtistIdRef.current = null;
        });
      return;
    }
    
    // Banking routes only: existing behavior unchanged
    if (isBankingRoute) {
      // Check if artist changed
      const artistChanged = lastFetchedArtistIdRef.current !== null && 
                           lastFetchedArtistIdRef.current !== currentArtistId;
      
      console.log('[useUnitJwt] Banking route - artistChanged:', artistChanged, 'lastFetched:', lastFetchedArtistIdRef.current, 'current:', currentArtistId, 'isFetching:', isFetching);
      
      if (artistChanged) {
        console.log('[useUnitJwt] 🔄 Artist changed from', lastFetchedArtistIdRef.current, 'to', currentArtistId, '- clearing state and cache');
        setJwt('');
        setJwtFromApi(false);
        setJwtArtistId(null);
        setIsFetching(false);
        lastFetchedArtistIdRef.current = null;
        localStorage.removeItem(getTokenKey(currentUserId));
        localStorage.removeItem(getExpKey(currentUserId));
        localStorage.removeItem(getCustomerTokenKey(currentUserId));
        localStorage.removeItem(getCustomerTokenExpKey(currentUserId));
        localStorage.removeItem(getApiTypeKey(currentUserId));
        localStorage.removeItem('unitCustomerToken');
      } else {
        // Artist unchanged: try to reuse valid token from localStorage to avoid calling /jwt every load
        const existingToken = localStorage.getItem(getTokenKey(currentUserId));
        const existingExp = localStorage.getItem(getExpKey(currentUserId));
        if (existingToken && existingExp) {
          const expTime = parseInt(existingExp);
          const currentTime = Date.now();
          const buffer = 5 * 60 * 1000; // 5 min
          if (expTime > currentTime + buffer) {
            console.log('[useUnitJwt] ✅ Banking: reusing valid JWT from localStorage for artist', currentArtistId);
            lastFetchedArtistIdRef.current = currentArtistId;
            setJwt(existingToken);
            setJwtFromApi(true);
            setJwtArtistId(currentArtistId);
            return;
          }
        }
      }
      
      if (jwt && jwtFromApi && jwtArtistId === currentArtistId && !isFetching) {
        return;
      }
      if (isFetching && lastFetchedArtistIdRef.current === currentArtistId) {
        return;
      }
      if (artistChanged || !jwt || jwtArtistId !== currentArtistId || !jwtFromApi) {
        setJwt('');
        setJwtFromApi(false);
        setJwtArtistId(null);
      }
      
      setIsFetching(true);
      const artistIdForFetch = currentArtistId;
      lastFetchedArtistIdRef.current = currentArtistId;
      
      getUnitJwtToken(true, artistIdForFetch, currentUserId)
        .then(({ token, exp }) => {
          if (currentArtistId !== artistIdForFetch) {
            setIsFetching(false);
            setJwt('');
            setJwtFromApi(false);
            setJwtArtistId(null);
            return;
          }
          if (!token || token.trim() === '') {
            setIsFetching(false);
            setJwt('');
            setJwtFromApi(false);
            setJwtArtistId(null);
            return;
          }
          setJwt(token);
          setJwtFromApi(true);
          setJwtArtistId(artistIdForFetch);
          setIsFetching(false);
          localStorage.removeItem(getCustomerTokenKey(currentUserId));
          localStorage.removeItem(getCustomerTokenExpKey(currentUserId));
          localStorage.removeItem('unitCustomerToken');
          const expMs = exp * 1000;
          localStorage.setItem(getTokenKey(currentUserId), token);
          localStorage.setItem(getExpKey(currentUserId), expMs.toString());
          if (userProfile?.id) {
            getUnitCoAccountDetails(userProfile.id, artistIdForFetch)
              .then(accountDetails => {
                if (accountDetails.data.accountExists) {
                  callUnitCustomerToken(token, currentUserId, 0, true).catch(err => {
                    console.error('[useUnitJwt] Failed to get customer token:', err);
                  });
                } else {
                  localStorage.removeItem(getCustomerTokenKey(currentUserId));
                  localStorage.removeItem(getCustomerTokenExpKey(currentUserId));
                  localStorage.removeItem('unitCustomerToken');
                }
              })
              .catch(err => {
                const errorStatus = err?.error?.status || err?.status;
                if (errorStatus !== 404) {
                  callUnitCustomerToken(token, currentUserId, 0, true).catch(() => null);
                } else {
                  localStorage.removeItem(getCustomerTokenKey(currentUserId));
                  localStorage.removeItem(getCustomerTokenExpKey(currentUserId));
                  localStorage.removeItem('unitCustomerToken');
                }
              });
          }
        })
        .catch((error: any) => {
          console.error('[useUnitJwt] Banking JWT fetch failed:', error);
          setIsFetching(false);
          setJwt('');
          setJwtFromApi(false);
          setJwtArtistId(null);
          lastFetchedArtistIdRef.current = null;
        });
    } else {
      // Non-white-label routes - use cache if available
      const lastArtistIdKey = `last_artist_id_${currentUserId}`;
      const lastArtistId = localStorage.getItem(lastArtistIdKey);
      const artistChanged = lastArtistId && lastArtistId !== currentArtistId.toString();
      
      if (artistChanged) {
        localStorage.removeItem(getTokenKey(currentUserId));
        localStorage.removeItem(getExpKey(currentUserId));
        localStorage.removeItem(getCustomerTokenKey(currentUserId));
        localStorage.removeItem(getCustomerTokenExpKey(currentUserId));
        localStorage.removeItem(getApiTypeKey(currentUserId));
        localStorage.removeItem('unitCustomerToken');
      }
      
      localStorage.setItem(lastArtistIdKey, currentArtistId.toString());
      
      const existingToken = localStorage.getItem(getTokenKey(currentUserId));
      const existingExp = localStorage.getItem(getExpKey(currentUserId));
      
      if (existingToken && existingExp && !artistChanged) {
        const expTime = parseInt(existingExp);
        const currentTime = Date.now();
        const buffer = 5 * 60 * 1000;
        
        if (expTime > currentTime + buffer) {
          setJwt(existingToken);
          return;
        }
      }
      
      // Fetch new token
      if (!isFetching) {
        setIsFetching(true);
        getUnitJwtToken(false, currentArtistId, currentUserId)
          .then(({ token, exp }) => {
            const expMs = exp * 1000;
            localStorage.setItem(getTokenKey(currentUserId), token);
            localStorage.setItem(getExpKey(currentUserId), expMs.toString());
            setJwt(token);
            setIsFetching(false);
            
            if (userProfile?.id) {
              callUnitCustomerToken(token, currentUserId, 0, false).catch(err => {
                console.error('[useUnitJwt] Failed to get customer token:', err);
              });
            }
          })
          .catch((error: any) => {
            console.error('[useUnitJwt] Failed to fetch JWT:', error);
            setIsFetching(false);
          });
      }
    }
    
    return () => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
    };
  }, [pathname, shouldFetch, currentUserId, currentArtistId, userProfile?.id]); // Dependencies - don't include state to avoid loops

  // Return JWT only if it matches current artist (for white label routes)
  const isWhiteLabelRoute = pathname === '/banking' || pathname === '/banking/activities' || pathname === '/dashboard' || pathname === '/analytics';
  let jwtToReturn = jwt;
  
  if (isWhiteLabelRoute) {
    console.log('[useUnitJwt] Return check - jwtFromApi:', jwtFromApi, 'jwt exists:', !!jwt, 'jwtArtistId:', jwtArtistId, 'currentArtistId:', currentArtistId, 'isFetching:', isFetching);
    
    if (jwtFromApi && jwt && jwtArtistId === currentArtistId && !isFetching) {
      jwtToReturn = jwt;
      console.log('[useUnitJwt] ✅ Returning JWT for artist:', currentArtistId);
    } else {
      jwtToReturn = '';
      if (jwt && (!jwtFromApi || jwtArtistId !== currentArtistId)) {
        console.log('[useUnitJwt] ⚠️ Clearing stale JWT - jwtFromApi:', jwtFromApi, 'jwtArtistId:', jwtArtistId, 'currentArtistId:', currentArtistId);
        setJwt('');
        setJwtFromApi(false);
        setJwtArtistId(null);
      }
    }
  }
  
  return { jwt: jwtToReturn, currentUserId };
}

// Keep track of ongoing requests to prevent duplicate calls
const ongoingRequests = new Map<string, AbortController>();

export const callUnitCustomerToken = async (jwtToken: string | null, userId?: string, retryCount: number = 0, forceRefresh: boolean = false): Promise<void> => {
  if (!userId) {
    return;
  }

  const CUSTOMER_TOKEN_KEY = getCustomerTokenKey(userId);
  const TOKEN_KEY = getTokenKey(userId);
  const API_TYPE_KEY = getApiTypeKey(userId);
  
  const currentPath = window.location.pathname;
  const isDashboardRoute = currentPath === '/dashboard' || currentPath === '/analytics';
  const isBankingRoute = currentPath === '/banking' || currentPath === '/banking/activities';
  const isWhiteLabelRoute = isDashboardRoute || isBankingRoute;
  
  // Clear cached customer token only for banking or when forceRefresh (dashboard may have just validated cache and skipped)
  if (isBankingRoute || forceRefresh) {
    console.log('[callUnitCustomerToken] Clearing cached customer token (banking or forceRefresh)');
    localStorage.removeItem(CUSTOMER_TOKEN_KEY);
    localStorage.removeItem(getCustomerTokenExpKey(userId));
  }
  
  let unitCustomerToken: string | null = null;
  if (!isWhiteLabelRoute && !forceRefresh) {
    unitCustomerToken = localStorage.getItem(CUSTOMER_TOKEN_KEY);
    if (!jwtToken) {
      jwtToken = localStorage.getItem(TOKEN_KEY);
    }
  }

  if (!jwtToken || jwtToken.trim() === '') {
    return;
  }

  if (isWhiteLabelRoute || forceRefresh) {
    console.log('[callUnitCustomerToken] Fetching fresh customer token');
  } else if (unitCustomerToken && unitCustomerToken !== 'undefined') {
    return;
  }

  if (ongoingRequests.has(userId)) {
    console.log('[callUnitCustomerToken] Request already in progress for user:', userId);
    return;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    
    ongoingRequests.set(userId, controller);

    const response = await fetch(process.env.REACT_APP_UNITCO_API_BASE_URL + '/app', {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${jwtToken}`,
      },
      signal: controller.signal
    });

    clearTimeout(timeoutId);
    ongoingRequests.delete(userId);

    if (response.status === 401) {
      console.error('[callUnitCustomerToken] 401 - JWT token invalid');
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(getExpKey(userId));
      localStorage.removeItem(CUSTOMER_TOKEN_KEY);
      localStorage.removeItem(getCustomerTokenExpKey(userId));
      localStorage.removeItem(API_TYPE_KEY);
      localStorage.removeItem('unitCustomerToken');
      return;
    }

    if (!response.ok) {
      console.error(`[callUnitCustomerToken] Error: ${response.status} ${response.statusText}`);
      
      // Dispatch error event for white label routes (especially 404 - account doesn't exist)
      if (isWhiteLabelRoute || forceRefresh) {
        const errorEvent = new CustomEvent('unitcoCustomerTokenError', {
          detail: { userId, status: response.status, message: response.statusText }
        });
        window.dispatchEvent(errorEvent);
      }
      
      return;
    }

    const data = await response.json();
    const customerToken = data?.data?.attributes?.customerToken;
    const apiType = data?.data?.type;

    if (!customerToken || customerToken === 'undefined') {
      // console.error('[callUnitCustomerToken] No customer token in response');
      console.warn('[callUnitCustomerToken] No customer token in response');
      
      // Dispatch error event for white label routes (no token = likely no account)
      if (isWhiteLabelRoute || forceRefresh) {
        const errorEvent = new CustomEvent('unitcoCustomerTokenError', {
          detail: { userId, status: 404, message: 'No customer token in response' }
        });
        window.dispatchEvent(errorEvent);
      }
      
      return;
    }

    if (isWhiteLabelRoute || forceRefresh) {
      // Dashboard: store customer token + expiry so next load can use from storage without API
      if (isDashboardRoute) {
        const CUSTOMER_TOKEN_EXP_MS = 24 * 60 * 60 * 1000; // 24h
        localStorage.setItem(CUSTOMER_TOKEN_KEY, customerToken);
        localStorage.setItem(getCustomerTokenExpKey(userId), (Date.now() + CUSTOMER_TOKEN_EXP_MS).toString());
        if (apiType) {
          localStorage.setItem(API_TYPE_KEY, apiType);
        }
      }
      console.log('[callUnitCustomerToken] ✅ White label route - dispatching customer token event');
      const tokenEvent = new CustomEvent('unitcoCustomerTokenReady', {
        detail: { userId, customerToken, apiType }
      });
      window.dispatchEvent(tokenEvent);
    } else {
      localStorage.setItem(CUSTOMER_TOKEN_KEY, customerToken);
      if (apiType) {
        localStorage.setItem(API_TYPE_KEY, apiType);
      }
      
      const event = new CustomEvent('localStorageChange', {
        detail: { key: CUSTOMER_TOKEN_KEY, value: customerToken, userId }
      });
      window.dispatchEvent(event);
    }
  } catch (error: any) {
    ongoingRequests.delete(userId);
    
    if (error.name === 'AbortError') {
      console.error('[callUnitCustomerToken] Request timed out:', error);
    } else {
      console.error('[callUnitCustomerToken] Error:', error);
      
      // Dispatch error event for white label routes if it's a 404 or network error
      if (isWhiteLabelRoute || forceRefresh) {
        const errorStatus = error?.status || (error?.response?.status) || 500;
        const errorEvent = new CustomEvent('unitcoCustomerTokenError', {
          detail: { userId, status: errorStatus, message: error?.message || 'Failed to fetch customer token' }
        });
        window.dispatchEvent(errorEvent);
      }
    }
  }
};
